import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search, FileText, X, ChevronDown, ChevronUp } from "lucide-react";
import { Link } from "wouter";
import { apiRequest, queryClient } from "@/lib/queryClient";
import type { Note, NoteLink } from "@shared/schema";
import { formatDistanceToNow } from "date-fns";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TiptapLink from "@tiptap/extension-link";
import Highlight from "@tiptap/extension-highlight";
import TextAlign from "@tiptap/extension-text-align";
import Subscript from "@tiptap/extension-subscript";
import Superscript from "@tiptap/extension-superscript";

interface LinkedNotesListProps {
  entityType: 'goal' | 'habit' | 'task';
  entityId: string;
  showSearch?: boolean;
  onCreateNote?: () => void;
}

function isSafeUrl(href: string): boolean {
  const normalized = href.trim().toLowerCase();
  return normalized.startsWith('https://') || normalized.startsWith('http://');
}

function RichContentRenderer({ content }: { content: any }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      TiptapLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
      Highlight.configure({
        multicolor: true,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Subscript,
      Superscript,
    ],
    content,
    editable: false,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none dark:prose-invert',
      },
      handleClick(_view, _pos, event) {
        const target = event.target as HTMLElement;
        const link = target.closest('a');
        if (link) {
          const href = link.getAttribute('href') || '';
          if (isSafeUrl(href)) {
            window.open(href, '_blank', 'noopener,noreferrer');
          }
          return true;
        }
        return false;
      },
    },
  });

  if (!editor) {
    return <div className="text-sm text-muted-foreground">Loading...</div>;
  }

  return <EditorContent editor={editor} />;
}

export default function LinkedNotesList({
  entityType,
  entityId,
  showSearch = false,
  onCreateNote,
}: LinkedNotesListProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);

  // Fetch notes linked to this entity
  const { data: notes = [], isLoading } = useQuery<Note[]>({
    queryKey: ["/api", entityType, entityId, "notes", searchQuery],
    queryFn: async () => {
      const params = searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : '';
      const response = await fetch(`/api/${entityType}/${entityId}/notes${params}`);
      if (!response.ok) throw new Error('Failed to fetch notes');
      return response.json();
    },
  });

  // Fetch note links to get linkId for unlinking
  const { data: noteLinks = [] } = useQuery<NoteLink[]>({
    queryKey: ["/api/notes/links", entityType, entityId],
    queryFn: async () => {
      const allLinks: NoteLink[] = [];
      for (const note of notes) {
        const response = await fetch(`/api/notes/${note.id}/links`);
        if (response.ok) {
          const links = await response.json();
          allLinks.push(...links.filter((link: NoteLink) => 
            link.entityType === entityType && link.entityId === entityId
          ));
        }
      }
      return allLinks;
    },
    enabled: notes.length > 0,
  });

  const unlinkNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      // Find the link ID for this note
      const link = noteLinks.find(l => l.noteId === noteId);
      if (!link) throw new Error('Link not found');
      
      await apiRequest("DELETE", `/api/notes/links/${link.id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api", entityType, entityId, "notes"] });
      queryClient.invalidateQueries({ queryKey: ["/api/notes/links"] });
    },
    onError: () => {
    },
  });

  const getContentText = (content: any): string => {
    if (typeof content === 'string') {
      return content;
    }
    if (content && typeof content === 'object') {
      if (content.text) return content.text;
      if (content.ops) {
        const text = content.ops.map((op: any) => op.insert || '').join('');
        return text;
      }
    }
    return '';
  };

  const getContentPreview = (content: any): string => {
    return getContentText(content).slice(0, 100);
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Linked Notes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Linked Notes
            {notes.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {notes.length}
              </Badge>
            )}
          </CardTitle>
          {notes.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-1 text-muted-foreground hover:text-foreground"
              data-testid="button-toggle-notes-expand"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="h-4 w-4" />
                  <span className="text-xs">Collapse</span>
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4" />
                  <span className="text-xs">Expand All</span>
                </>
              )}
            </Button>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {showSearch && notes.length > 0 && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search linked notes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="input-search-linked-notes"
            />
          </div>
        )}

        {notes.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 mx-auto mb-3 text-muted-foreground opacity-50" />
            <p className="text-sm text-muted-foreground mb-4">
              No notes linked yet
            </p>
            {onCreateNote && (
              <Button onClick={onCreateNote} variant="outline" size="sm">
                Create Note
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {notes.map((note) => {
              const preview = getContentPreview(note.plainContent || note.content);
              
              return (
                <Card key={note.id} className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <Link href={`/notepad?note=${note.id}`}>
                          <h4
                            className="font-medium text-sm hover:text-primary cursor-pointer truncate mb-1"
                            data-testid={`link-note-${note.id}`}
                          >
                            {note.title}
                          </h4>
                        </Link>
                        
                        {isExpanded ? (
                          note.content ? (
                            <div className="mb-2 max-h-96 overflow-y-auto border rounded-md p-3 bg-muted/30">
                              <RichContentRenderer content={note.content} />
                            </div>
                          ) : null
                        ) : (
                          preview ? (
                            <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                              {preview}
                              {preview.length === 100 && '...'}
                            </p>
                          ) : null
                        )}
                        
                        {note.tags && note.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {note.tags.slice(0, 3).map((tag) => (
                              <Badge
                                key={tag}
                                variant="secondary"
                                className="text-xs px-1.5 py-0"
                              >
                                {tag}
                              </Badge>
                            ))}
                            {note.tags.length > 3 && (
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                +{note.tags.length - 3}
                              </Badge>
                            )}
                          </div>
                        )}
                        
                        <div className="text-xs text-muted-foreground">
                          {formatDistanceToNow(new Date(note.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => unlinkNoteMutation.mutate(note.id)}
                        disabled={unlinkNoteMutation.isPending}
                        className="flex-shrink-0"
                        data-testid={`button-unlink-note-${note.id}`}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
